// chartFigures.js
// Every chart type's figure builder in one map, for the code that needs a
// chart's plotly figure WITHOUT rendering it — the combined chart's whole-image
// export. The renderers themselves call their own builder directly.
import { buildBarFigure } from './BarChart';
import { buildLineFigure } from './LineChart';
import { buildPieFigure } from './PieChart';
import { buildScatterFigure } from './ScatterChart';
import { buildBoxFigure } from './BoxAndWhiskerChart';
import { chartAxisError } from '../../utils/chartTheme';

const FIGURE_BUILDERS = {
  bar: buildBarFigure,
  line: buildLineFigure,
  pie: buildPieFigure,
  scatter: buildScatterFigure,
  box_and_whisker: buildBoxFigure,
};

// `{ data, layout }` for one leaf chart. A chart the browser cannot draw
// faithfully (an unsupported axis scale) cannot be exported faithfully either,
// so it throws the same message the renderer shows in its place.
export const buildChartFigure = (chart, context) => {
  const build = FIGURE_BUILDERS[chart?.type];
  if (!build) throw new Error(`Unsupported chart type: ${chart?.type || 'unknown'}`);
  const axisError = chartAxisError(chart);
  if (axisError) throw new Error(axisError);
  return build(chart, context);
};

// The leaves of a combined chart in reading order, each with the titles of the
// combined charts between the root and it (the root's own title is printed
// once, above the grid). Nested combined charts flatten into the same list.
export const flattenChartLeaves = (chart, ancestorTitles = []) => (
  (Array.isArray(chart?.elements) ? chart.elements : []).flatMap((child) => {
    if (child?.type === 'superchart') {
      const title = typeof child.title === 'string' && child.title.trim() ? child.title.trim() : null;
      return flattenChartLeaves(child, title ? [...ancestorTitles, title] : ancestorTitles);
    }
    return [{ chart: child, ancestorTitles }];
  })
);
